// Convert Excel column letters to 0-based column number (A=0, B=1, ..., Z=25, AA=26, AB=27, etc.)
export const columnLettersToNumber = (columnLetters: string): number => {
    let result = 0
    for (let i = 0; i < columnLetters.length; i++) {
        result = result * 26 + (columnLetters.charCodeAt(i) - 64)
    }
    return result - 1 // Convert to 0-based
}

// Convert 0-based column number to Excel column letters (0=A, 1=B, ..., 25=Z, 26=AA, 27=AB, etc.)
export const columnNumberToLetters = (columnNumber: number): string => {
    columnNumber += 1 // Convert to 1-based for calculation
    let result = ''
    while (columnNumber > 0) {
        columnNumber--
        result = String.fromCharCode(65 + (columnNumber % 26)) + result
        columnNumber = Math.floor(columnNumber / 26)
    }
    return result
}

// Normalize cell address by removing $ signs
export const normalizeCellAddress = (cellAddress: string): string => {
    return cellAddress.replace(/\$/g, '')
}

// Check if a parameter is a range reference (e.g., A1:B10)
export const isRangeReference = (param: string): boolean => {
    const cleanParam = param.replace(/^["']|["']$/g, '')
    return /^\$?[A-Z]+\$?\d+:\$?[A-Z]+\$?\d+$/.test(cleanParam)
}

// Check if a parameter is an array literal (e.g., {"AAPL", "MSFT"})
export const isArrayLiteral = (param: string): boolean => {
    const trimmed = param.trim()
    return trimmed.startsWith('{') && trimmed.endsWith('}')
}

// Parse array literal into individual values
export const parseArrayLiteral = (param: string): string[] => {
    // Remove outer braces and split by comma
    const content = param.trim().slice(1, -1)
    // Split by comma, handling quoted strings
    const values: string[] = []
    let current = ''
    let inQuotes = false

    for (let i = 0; i < content.length; i++) {
        const char = content[i]
        if (char === '"') {
            inQuotes = !inQuotes
        } else if (char === ',' && !inQuotes) {
            values.push(current.trim().replace(/^["']|["']$/g, ''))
            current = ''
        } else {
            current += char
        }
    }

    if (current.trim()) {
        values.push(current.trim().replace(/^["']|["']$/g, ''))
    }

    return values.filter((v) => v !== '')
}

// Expand a range reference into individual cell addresses
export const expandRangeToCells = (rangeAddress: string): string[] => {
    const cleanRange = rangeAddress.replace(/\$/g, '')
    const [start, end] = cleanRange.split(':')

    const startMatch = start.match(/([A-Z]+)(\d+)/)
    const endMatch = end.match(/([A-Z]+)(\d+)/)

    if (!startMatch || !endMatch) return []

    const startCol = startMatch[1]
    const startRow = parseInt(startMatch[2])
    const endCol = endMatch[1]
    const endRow = parseInt(endMatch[2])

    const startColCode = columnLettersToNumber(startCol)
    const endColCode = columnLettersToNumber(endCol)

    const cells: string[] = []

    for (let col = startColCode; col <= endColCode; col++) {
        for (let row = startRow; row <= endRow; row++) {
            const cellAddress = `${columnNumberToLetters(col)}${row}`
            cells.push(cellAddress)
        }
    }

    return cells
}

const isFormulaIdentifierCharacter = (char: string): boolean =>
    /^[A-Z0-9_]$/i.test(char)

const findFunctionOpeningParen = (
    formula: string,
    functionName: string,
    requireDirectFormulaStart: boolean
): number | null => {
    const upperFormula = formula.toUpperCase()
    const upperFunctionName = functionName.toUpperCase()
    const functionNames = [
        upperFunctionName,
        `_XLFN.${upperFunctionName}`,
        `_XLDUF.${upperFunctionName}`,
    ]
    let inQuotes = false

    for (let i = 0; i < formula.length; i++) {
        const char = formula[i]

        if (char === '"') {
            if (inQuotes && formula[i + 1] === '"') {
                i++
                continue
            }
            inQuotes = !inQuotes
            continue
        }

        if (inQuotes) {
            continue
        }

        for (const candidateName of functionNames) {
            if (!upperFormula.startsWith(candidateName, i)) {
                continue
            }

            const previousChar = i > 0 ? formula[i - 1] : ''
            if (previousChar && isFormulaIdentifierCharacter(previousChar)) {
                continue
            }

            if (requireDirectFormulaStart) {
                const prefix = formula.slice(0, i).trim()
                if (prefix !== '=') {
                    continue
                }
            }

            let openingParenIndex = i + candidateName.length
            while (
                openingParenIndex < formula.length &&
                /\s/.test(formula[openingParenIndex])
            ) {
                openingParenIndex++
            }

            if (formula[openingParenIndex] === '(') {
                return openingParenIndex
            }
        }
    }

    return null
}

const parseFunctionParamsFromOpeningParen = (
    formula: string,
    openingParenIndex: number
): string[] => {
    const startIndex = openingParenIndex + 1
    let depth = 1
    let current = ''
    const params: string[] = []
    let inQuotes = false
    let inBraces = false

    for (let i = startIndex; i < formula.length; i++) {
        const char = formula[i]

        if (char === '"') {
            if (inQuotes && formula[i + 1] === '"') {
                current += char + formula[i + 1]
                i++
                continue
            }
            inQuotes = !inQuotes
            current += char
        } else if (!inQuotes) {
            if (char === '{') {
                inBraces = true
                current += char
            } else if (char === '}') {
                inBraces = false
                current += char
            } else if (char === '(') {
                depth++
                current += char
            } else if (char === ')') {
                depth--
                if (depth === 0) {
                    if (current.trim()) {
                        params.push(current.trim())
                    }
                    break
                }
                current += char
            } else if (char === ',' && !inBraces) {
                params.push(current.trim())
                current = ''
            } else {
                current += char
            }
        } else {
            current += char
        }
    }

    return params
}

// Parse WISE/WISEPRICE formula parameters
export const parseWiseFormulaParams = (
    formula: string,
    functionName: string,
    minParams: number = 2
): string[] | null => {
    const openingParenIndex = findFunctionOpeningParen(
        formula,
        functionName,
        true
    )
    if (openingParenIndex === null) return null

    const params = parseFunctionParamsFromOpeningParen(
        formula,
        openingParenIndex
    )

    return params.length >= minParams ? params : null
}

export const parseFormulaFunctionParams = (
    formula: string,
    functionName: string,
    minParams: number = 0
): string[] | null => {
    const openingParenIndex = findFunctionOpeningParen(
        formula,
        functionName,
        false
    )
    if (openingParenIndex === null) return null

    const params = parseFunctionParamsFromOpeningParen(
        formula,
        openingParenIndex
    )

    return params.length >= minParams ? params : null
}

export const shouldRefreshWiseOptionsFormula = (formula: string): boolean => {
    const params = parseFormulaFunctionParams(formula, 'WISEOPTIONS', 3)
    return params?.length === 3
}

// Parse arithmetic operations appended to formulas (e.g., =WISE(...)*100)
export const parseArithmeticOperation = (
    formula: string,
    functionName: string
): ((value: any) => any) | null => {
    try {
        // More robust pattern that handles nested parentheses
        // Start from the beginning and find the matching closing parenthesis
        const startPattern = new RegExp(`=${functionName}\\(`, 'i')
        const startMatch = formula.match(startPattern)

        if (!startMatch || startMatch.index === undefined) {
            return null
        }

        const startIndex = startMatch.index + startMatch[0].length
        let depth = 1
        let endIndex = startIndex

        // Find the matching closing parenthesis
        for (let i = startIndex; i < formula.length && depth > 0; i++) {
            const char = formula[i]
            if (char === '(') depth++
            else if (char === ')') depth--
            endIndex = i
        }

        // Get everything after the closing parenthesis
        const operation = formula.substring(endIndex + 1).trim()

        if (!operation) {
            return null
        }

        // Validate that the operation only contains safe arithmetic characters
        // Only allow: numbers, arithmetic operators, parentheses, dots, and whitespace
        // Reject any strings with quotes, letters, or other unsafe characters
        if (!/^[\d\s+\-*/.()]+$/.test(operation)) {
            console.warn(
                '[parseArithmeticOperation] Invalid operation characters detected:',
                operation
            )
            return null
        }

        // Additional safety check: ensure no quotes or text literals
        if (
            operation.includes('"') ||
            operation.includes("'") ||
            operation.includes('`')
        ) {
            console.warn(
                '[parseArithmeticOperation] Operation contains quotes/strings:',
                operation
            )
            return null
        }

        // Parse and return a function that applies the arithmetic operation
        return (value: any) => {
            if (value === null || value === undefined || value === '') {
                return value
            }

            // Handle "Loading..." and error strings
            if (
                typeof value === 'string' &&
                (value === 'Loading...' || value.includes('Error'))
            ) {
                return value
            }

            try {
                // Convert value to number if possible
                const numValue =
                    typeof value === 'number' ? value : parseFloat(value)

                if (isNaN(numValue)) {
                    return value
                }

                // Evaluate the operation safely with proper error handling
                // Support basic arithmetic: +, -, *, /
                const result = Function(
                    '"use strict"; return (' + numValue + operation + ')'
                )()

                // Validate result is a number
                if (typeof result !== 'number' || isNaN(result)) {
                    return value
                }

                return result
            } catch (error) {
                // Log error but don't spam - this is expected for some edge cases
                console.log(
                    '[parseArithmeticOperation] Error applying operation:',
                    operation,
                    'to value:',
                    value,
                    'Error:',
                    error instanceof Error ? error.message : error
                )
                return value
            }
        }
    } catch (error) {
        // If parsing fails entirely, log and return null
        console.warn(
            '[parseArithmeticOperation] Failed to parse formula:',
            formula,
            'Error:',
            error instanceof Error ? error.message : error
        )
        return null
    }
}

// Check if starting cell overlaps with any parameter cells
export const doesStartingCellOverlapParameters = (
    startingCell: string,
    params: Array<string | null | undefined>,
    cellRefRegex: RegExp
): boolean => {
    const startingCellNormalized = normalizeCellAddress(startingCell)

    return params.some((param) => {
        if (!param) {
            return false
        }

        // Array literals don't overlap with cells
        if (isArrayLiteral(param)) {
            return false
        }

        // Handle range parameters
        if (isRangeReference(param)) {
            const cells = expandRangeToCells(param)
            return cells.some(
                (cell) => normalizeCellAddress(cell) === startingCellNormalized
            )
        }

        // Handle single cell parameters
        return (
            cellRefRegex.test(param) &&
            normalizeCellAddress(param) === startingCellNormalized
        )
    })
}

// Extract column letter(s) from a cell address
// Handles addresses with/without worksheet prefix, with/without $ signs
export const extractColumnFromAddress = (address: string): string => {
    // Remove worksheet prefix if present (e.g., "Sheet1!A1" -> "A1")
    const cleanAddress = address.includes('!') ? address.split('!')[1] : address

    // Remove $ signs
    const normalized = normalizeCellAddress(cleanAddress)

    // Extract column letters (e.g., "A1" -> "A", "AA10" -> "AA")
    const match = normalized.match(/([A-Z]+)/)
    return match ? match[1] : ''
}

// Calculate data range from ticker rows and data column
// Returns range like "B3:B102" or "B3:D102" based on ticker rows, data column, and number of columns
export const calculateDataRange = (
    tickerRows: number[],
    dataColumn: string,
    numColumns: number = 1
): string | null => {
    if (tickerRows.length === 0) return null

    const minRow = Math.min(...tickerRows)
    const maxRow = Math.max(...tickerRows)

    if (numColumns === 1) {
        return `${dataColumn}${minRow}:${dataColumn}${maxRow}`
    }

    // Calculate end column
    const startColCode = columnLettersToNumber(dataColumn)
    const endColCode = startColCode + numColumns - 1
    const endColumn = columnNumberToLetters(endColCode)

    return `${dataColumn}${minRow}:${endColumn}${maxRow}`
}

// Extract row numbers from a range (e.g., "B3:B102" -> [3, 4, 5, ..., 102])
export const extractRowsFromRange = (range: string): number[] => {
    const cleanRange = range.replace(/\$/g, '')
    const match = cleanRange.match(/([A-Z]+)(\d+):([A-Z]+)(\d+)/)
    if (!match) return []

    const startRow = parseInt(match[2])
    const endRow = parseInt(match[4])

    const rows: number[] = []
    for (let row = startRow; row <= endRow; row++) {
        rows.push(row)
    }
    return rows
}

// Extract row number from a cell address (e.g., "B7" -> 7)
export const extractRowFromAddress = (address: string): number | null => {
    const cleanAddress = address.replace(/\$/g, '')
    const match = cleanAddress.match(/([A-Z]+)(\d+)/)
    if (!match) return null
    return parseInt(match[2])
}
